import { useEffect, useRef } from "react";
import { useAppSelector } from "./redux";
import { cdlRepository, timeProvider } from "../AppController";
import { useDashboardStats } from "./useDashboardStats";

export const useStatsTimeout = () => {
  const {
    stats,
    statsHR,
    getStatsData,
    getStatsDataHR,
    removeStatsData,
    removeStatsDataHR,
  } = useDashboardStats({
    cdlRepository: cdlRepository,
    timeProvider,
  });
  const { statsTimeout, isPinAuthorized } = useAppSelector((state) => ({
    statsTimeout: state.dashboard.statsTimeout,
    isPinAuthorized: state.auth.isPinAuthorized,
  }));

  // Use refs to prevent stale closures and avoid dependency issues
  const statsRef = useRef(stats);
  const statsHRRef = useRef(statsHR);
  const getStatsDataRef = useRef(getStatsData);
  const getStatsDataHRRef = useRef(getStatsDataHR);
  const removeStatsDataRef = useRef(removeStatsData);
  const removeStatsDataHRRef = useRef(removeStatsDataHR);

  useEffect(() => {
    statsRef.current = stats;
    statsHRRef.current = statsHR;
    getStatsDataRef.current = getStatsData;
    getStatsDataHRRef.current = getStatsDataHR;
    removeStatsDataRef.current = removeStatsData;
    removeStatsDataHRRef.current = removeStatsDataHR;
  }, [
    stats,
    statsHR,
    getStatsData,
    getStatsDataHR,
    removeStatsData,
    removeStatsDataHR,
  ]);

  const hasOneHourPassed = (unixTimestamp: number) => {
    return timeProvider
      .dateLibrary()
      .isAfter(timeProvider.dateLibrary.unix(unixTimestamp).add(1, "hour"));
  };

  useEffect(() => {
    if (!isPinAuthorized || !statsTimeout) return;

    const refreshStats = () => {
      const currentStats = statsRef.current;
      const currentStatsHR = statsHRRef.current;
      const patientIds = Object.keys({
        ...currentStats,
        ...currentStatsHR,
      }).map((item) => Number(item));

      removeStatsDataRef.current(patientIds);
      removeStatsDataHRRef.current(patientIds);
      getStatsDataRef.current(patientIds);
      getStatsDataHRRef.current(patientIds);
    };

    if (hasOneHourPassed(statsTimeout)) {
      refreshStats();
      return;
    }

    const expiresAt = timeProvider.dateLibrary
      .unix(statsTimeout)
      .add(1, "hour");
    const msUntilExpiry = expiresAt.diff(timeProvider.dateLibrary());

    if (msUntilExpiry <= 0) {
      refreshStats();
      return;
    }

    const timer = setTimeout(refreshStats, msUntilExpiry);
    return () => clearTimeout(timer);
  }, [statsTimeout, isPinAuthorized]);
};
